import React, {ReactNode, useCallback, useEffect, useLayoutEffect, useRef} from 'react';
import { FormState, useForm } from '../../form';

export interface ActiveAndDirtyFieldDetectorProps<T> {

   onActiveFieldChange: (oldActiveField: keyof T, newActiveField: keyof T | undefined) => void;

   onDirtyFieldChange: (dirtyField: keyof T) => void;

   children?: ReactNode;
}

export const ActiveAndDirtyFieldDetector = <T,>(props: ActiveAndDirtyFieldDetectorProps<T>) => {

   const form = useForm<T,T>();
   const activeFieldRef = useRef<keyof T | undefined>();

   const { onActiveFieldChange, onDirtyFieldChange } = props;

   const activeChangeHandler = useCallback((formState: FormState<T,Partial<T>>) => {
      const oldActiveField = activeFieldRef !== undefined ? activeFieldRef.current : undefined;
      const newActiveField = formState.active;

      if (oldActiveField !== undefined && oldActiveField !== newActiveField) {

         setTimeout(() => onActiveFieldChange(oldActiveField, newActiveField),0);
      }
      activeFieldRef.current = newActiveField;
   },[onActiveFieldChange]);

   const dirtyFieldsChangeHandler = useCallback((formState: FormState<T,Partial<T>>) => {
      let oldActiveField = activeFieldRef !== undefined ? activeFieldRef.current : undefined;
      const dirtyFields = Object.keys(formState.dirtyFields);
      if (dirtyFields.length > 0) {
         dirtyFields.forEach((dirtyField) => {
            if (oldActiveField === dirtyField) {
               oldActiveField = undefined;
               activeFieldRef.current = undefined;
            }
            setTimeout(() => onDirtyFieldChange(dirtyField as keyof T),0);
         });
      }
   },[onDirtyFieldChange]);

   useLayoutEffect(() => {
      setTimeout(()=>{
         const activeField = form.getState().active;
         if (activeField === undefined && document.activeElement && document.activeElement.getAttribute('name')) {
            try {
               form.focus(document.activeElement.getAttribute('name') as keyof T);
            }
            catch (error) {
               // Do nothing
            }
         }
         activeFieldRef.current = activeField;
      },100);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   },[]);

   useEffect(() => {
      const activeChangeHandlerUnsubscribe = form.subscribe(activeChangeHandler, { active: true });
      return () => {
         activeChangeHandlerUnsubscribe();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   },[activeChangeHandler]);

   useEffect(() => {
      const dirtyFieldsChangeHandlerUnsubscribe = form.subscribe(dirtyFieldsChangeHandler, { dirtyFields: true });
      return () => {
         dirtyFieldsChangeHandlerUnsubscribe();
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   },[dirtyFieldsChangeHandler]);

   return (
      <>
         {props.children}
      </>
   );
};
